'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { formatNumber } from '@lichtblick/localization';
import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { ChevronRightXs } from '@lichtblick/photon/icons/arrows/chevron-right-xs';
import { Co2OffM } from '@lichtblick/photon/icons/energy/co2-off-m';
import { PlantM } from '@lichtblick/photon/icons/objects/plant-m';
import { Stepper } from '@lichtblick/photon/molecules/stepper';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { useTracker } from '@lichtblick/tracker';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { B2bSpCommodity } from '../../../types/generated.lichtblick';
import { useVpCodes } from '../../OAllInOneCalculator/useVpCodes';
import styles from './all-in-one-calculator.module.css';
import { MAX_CONSUMPTION, MIN_CONSUMPTION, MIN_METERS } from './constants';
const CONFIG = {
    [B2bSpCommodity.Electricity]: {
        badge: /*#__PURE__*/ _jsxs("div", {
            className: classNames(styles.badge, styles['badge-electricity']),
            children: [
                /*#__PURE__*/ _jsx(PlantM, {}),
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx(Text, {
                            bold: true,
                            children: "100% Ökostrom"
                        }),
                        /*#__PURE__*/ _jsx(Text, {
                            size: "s",
                            children: "aus erneuerbaren Quellen"
                        })
                    ]
                })
            ]
        }),
        consumptionField: 'strom',
        consumptionLabel: 'Stromverbrauch',
        product: 'GewerbeStrom',
        url: '/checkout-b2b/gewerbestrom/'
    },
    [B2bSpCommodity.Gas]: {
        badge: /*#__PURE__*/ _jsxs("div", {
            className: classNames(styles.badge, styles['badge-gas']),
            children: [
                /*#__PURE__*/ _jsx(Co2OffM, {}),
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx(Text, {
                            bold: true,
                            children: "CO₂-Emissionen reduzieren"
                        }),
                        /*#__PURE__*/ _jsx(Text, {
                            size: "s",
                            children: "durch Beimischung von BioGas"
                        })
                    ]
                })
            ]
        }),
        consumptionField: 'gas',
        consumptionLabel: 'Gasverbrauch',
        product: 'GewerbeGas',
        url: '/checkout-b2b/gewerbegas/'
    }
};
export const CommoditySubcalculator = ({ commodity })=>{
    const { computeVpCodes } = useVpCodes();
    const { trackCustomEvent } = useTracker();
    const [vpCodes, setVpCodes] = useState({});
    const { badge, consumptionField, consumptionLabel, product, url } = CONFIG[commodity];
    useEffect(()=>setVpCodes(computeVpCodes()), [
        computeVpCodes
    ]);
    return /*#__PURE__*/ _jsxs("form", {
        action: url,
        method: "GET",
        "data-testid": `${commodity}-form`,
        onSubmit: ({ currentTarget })=>{
            if (currentTarget.checkValidity()) {
                const data = new FormData(currentTarget);
                trackCustomEvent({
                    eventName: 'b2b_calculator_show_lead_form',
                    customProps: {
                        product,
                        meter_counter: Number(data.get('zaehler')),
                        consumption_calculator: `${data.get(consumptionField)?.toString()} kWh/Jahr`
                    }
                });
            }
        },
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: styles['input-grid'],
                children: /*#__PURE__*/ _jsxs("div", {
                    className: styles['input-row-2'],
                    children: [
                        /*#__PURE__*/ _jsx(Stepper, {
                            label: "Anzahl Zähler",
                            min: MIN_METERS,
                            name: "zaehler",
                            hideLabelSuffix: true
                        }),
                        /*#__PURE__*/ _jsx(Textfield, {
                            inputMode: "numeric",
                            label: consumptionLabel,
                            max: MAX_CONSUMPTION,
                            min: MIN_CONSUMPTION,
                            name: consumptionField,
                            errorMessage: `Der Verbrauch muss zwischen ${formatNumber(MIN_CONSUMPTION)} und ${formatNumber(MAX_CONSUMPTION)} kWh/Jahr liegen.`,
                            rightSlot: /*#__PURE__*/ _jsx(Text, {
                                className: styles.unit,
                                size: "s",
                                children: "kWh/Jahr"
                            }),
                            type: "number",
                            required: true,
                            hideLabelSuffix: true
                        })
                    ]
                })
            }),
            Boolean(vpCodes.vp) && /*#__PURE__*/ _jsx("input", {
                name: "vp",
                type: "hidden",
                value: vpCodes.vp
            }),
            Boolean(vpCodes.uvp) && /*#__PURE__*/ _jsx("input", {
                name: "uvp",
                type: "hidden",
                value: vpCodes.uvp
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles['confirmation-row'],
                children: [
                    badge,
                    /*#__PURE__*/ _jsx(BoxCTA, {
                        as: "button",
                        rightSlot: /*#__PURE__*/ _jsx(ChevronRightXs, {}),
                        type: "submit",
                        "data-dd-action-name": `Calculator submit: ${product}`,
                        children: "Unverbindlich anfragen"
                    })
                ]
            })
        ]
    });
};

//# sourceMappingURL=commodity-subcalculator.js.map